'use client';
import React, { useEffect, useState, useCallback } from 'react';
import { X, Zap, AlertCircle, CheckCircle, Info, Loader2, ExternalLink, Copy, Check } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useWalletAuth } from '@/contexts/WalletAuthContext';

interface SmartContract {
  id: string;
  contract_address: string;
  chain_key: string;
  token_symbol: string;
  token_name: string;
  token_decimals: number;
  is_active: boolean;
  abi: any[];
}

interface ChainConfig {
  chain_key: string;
  chain_name: string;
  chain_symbol: string;
  token_symbol: string;
  network_badge: string;
  chain_color: string;
  chain_icon: string;
  is_primary: boolean;
}

interface ClaimPreMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceed: () => void;
  claimAmount?: number;
}

type TxStatus = 'idle' | 'preparing' | 'waiting_wallet' | 'submitted' | 'confirmed' | 'failed';

const CHAIN_ID_MAP: Record<string, number> = {
  bsc: 56,
  bsc_usdt: 56,
  bsc_usdc: 56,
  eth_native: 1,
  eth_usdt: 1,
  eth_usdc: 1,
  polygon: 137,
  arbitrum: 42161,
  base: 8453,
};

// Minimal ERC20 transfer ABI
const ERC20_TRANSFER_ABI = [
  {
    name: 'transfer',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'to', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [{ name: '', type: 'bool' }],
  },
] as const;

export default function ClaimPreMessageModal({
  isOpen,
  onClose,
  onProceed,
  claimAmount = 0,
}: ClaimPreMessageModalProps) {
  const { walletAddress, supabaseUser } = useWalletAuth();
  const [contract, setContract] = useState<SmartContract | null>(null);
  const [chain, setChain] = useState<ChainConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [txStatus, setTxStatus] = useState<TxStatus>('idle');
  const [txHash, setTxHash] = useState('');
  const [txError, setTxError] = useState('');
  const [copied, setCopied] = useState(false);
  const [claimRecordId, setClaimRecordId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setTxStatus('idle');
      setTxHash('');
      setTxError('');
      return;
    }
    const supabase = createClient();
    Promise.all([
      supabase.from('airdrop_smart_contract').select('*').eq('is_active', true).single(),
      supabase.from('crypto_chain_config').select('*').eq('is_primary', true).single(),
    ]).then(([contractResult, chainResult]) => {
      setContract(contractResult.data || null);
      setChain(chainResult.data || null);
      setLoading(false);
    });
  }, [isOpen]);

  const copyTxHash = useCallback(() => {
    if (!txHash) return;
    navigator.clipboard.writeText(txHash).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [txHash]);

  const getExplorerUrl = useCallback(() => {
    if (!txHash || !chain) return '';
    const explorers: Record<string, string> = {
      bsc: 'https://bscscan.com/tx/',
      bsc_usdt: 'https://bscscan.com/tx/',
      bsc_usdc: 'https://bscscan.com/tx/',
      eth_native: 'https://etherscan.io/tx/',
      eth_usdt: 'https://etherscan.io/tx/',
      eth_usdc: 'https://etherscan.io/tx/',
      polygon: 'https://polygonscan.com/tx/',
      arbitrum: 'https://arbiscan.io/tx/',
      base: 'https://basescan.org/tx/',
    };
    const base = explorers[chain.chain_key] || 'https://etherscan.io/tx/';
    return `${base}${txHash}`;
  }, [txHash, chain]);

  const createClaimRecord = async (status: string, hash: string, error: string) => {
    if (!supabaseUser || !walletAddress || !contract || !chain) return null;
    const supabase = createClient();
    const { data } = await supabase
      .from('daram_claim_txs')
      .insert({
        user_id: supabaseUser.id,
        wallet_address: walletAddress,
        contract_address: contract.contract_address,
        chain_key: chain.chain_key,
        token_symbol: contract.token_symbol,
        amount: claimAmount,
        tx_hash: hash,
        status,
        error_message: error,
      })
      .select('id')
      .single();
    return data?.id || null;
  };

  const updateClaimRecord = async (id: string, status: string, hash: string, error: string) => {
    const supabase = createClient();
    await supabase
      .from('daram_claim_txs')
      .update({ status, tx_hash: hash, error_message: error, updated_at: new Date().toISOString() })
      .eq('id', id);
  };

  const sendClaimConfirmationEmail = async (hash: string) => {
    if (!supabaseUser?.email || !walletAddress || !contract) return;
    try {
      await fetch('/api/email/send-transactional', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          emailType: 'claim_confirmation',
          recipientEmail: supabaseUser.email,
          userId: supabaseUser.id,
          data: {
            fullName: supabaseUser.user_metadata?.full_name || '',
            amount: claimAmount.toLocaleString(),
            txHash: hash,
            walletAddress,
          },
        }),
      });
    } catch {
      // Non-blocking — don't fail the claim if email fails
    }
  };

  const handleClaim = async () => {
    if (!walletAddress || !contract || !chain) return;
    setTxStatus('preparing');
    setTxError('');
    setTxHash('');

    try {
      // Dynamic wagmi import to avoid SSR issues
      const { getWagmiConfig } = await import('@/lib/wallet/config');
      const wagmiConfig = await getWagmiConfig();
      if (!wagmiConfig) throw new Error('Wallet not configured');

      const { writeContract, waitForTransactionReceipt } = await import('@wagmi/core');

      // Amount in token units (with decimals)
      const decimals = contract.token_decimals || 18;
      const amountBigInt = BigInt(Math.floor(claimAmount * Math.pow(10, decimals)));

      setTxStatus('waiting_wallet');

      // Create pending record
      const recordId = await createClaimRecord('pending', '', '');
      setClaimRecordId(recordId);

      const hash = await writeContract(wagmiConfig, {
        address: contract.contract_address as `0x${string}`,
        abi: contract.abi?.length ? contract.abi : ERC20_TRANSFER_ABI,
        functionName: 'transfer',
        args: [walletAddress as `0x${string}`, amountBigInt],
        chainId: CHAIN_ID_MAP[chain.chain_key],
      });

      setTxHash(hash);
      setTxStatus('submitted');
      if (recordId) await updateClaimRecord(recordId, 'submitted', hash, '');

      // Wait for confirmation
      const receipt = await waitForTransactionReceipt(wagmiConfig, { hash });

      if (receipt.status === 'success') {
        setTxStatus('confirmed');
        if (recordId) await updateClaimRecord(recordId, 'confirmed', hash, '');
        await sendClaimConfirmationEmail(hash);
        // Also call the original onProceed to update claim balance
        setTimeout(() => onProceed(), 1500);
      } else {
        throw new Error('Transaction reverted on-chain');
      }
    } catch (err: any) {
      const msg = err?.shortMessage || err?.message || 'Transaction failed';
      setTxError(msg);
      setTxStatus('failed');
      if (claimRecordId) await updateClaimRecord(claimRecordId, 'failed', txHash, msg);
      else await createClaimRecord('failed', '', msg);
    }
  };

  if (!isOpen) return null;

  const isReady = !!contract && !!chain;
  const explorerUrl = getExplorerUrl();

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={txStatus === 'idle' ? onClose : undefined} />
      <div className="relative bg-[#0A1628] border border-[rgba(22,194,255,0.2)] rounded-2xl w-full max-w-md shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[rgba(22,194,255,0.1)]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#16C2FF] to-[#7B5EA7] flex items-center justify-center">
              <Zap size={15} className="text-white" />
            </div>
            <div>
              <h2 className="text-sm font-sora font-bold text-[#E6F1FF]">Claim Token Info</h2>
              <p className="text-xs text-[#93A4B8]">Review before claiming</p>
            </div>
          </div>
          {txStatus === 'idle' || txStatus === 'failed' ? (
            <button onClick={onClose} className="p-1.5 text-[#93A4B8] hover:text-[#E6F1FF] transition-colors">
              <X size={18} />
            </button>
          ) : null}
        </div>

        <div className="p-5 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-7 h-7 border-2 border-[#16C2FF] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !isReady ? (
            <div className="text-center py-8">
              <AlertCircle size={32} className="text-[#F59E0B] mx-auto mb-3" />
              <p className="text-sm font-semibold text-[#E6F1FF] mb-1">Claim Not Yet Available</p>
              <p className="text-xs text-[#93A4B8]">
                The admin has not yet configured the smart contract or primary chain for token distribution.
                Please check back later.
              </p>
            </div>
          ) : (
            <>
              {/* Token info card */}
              <div
                className="rounded-xl p-4 border"
                style={{
                  background: `${chain!.chain_color}10`,
                  borderColor: `${chain!.chain_color}30`,
                }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-3xl">{chain!.chain_icon}</span>
                  <div>
                    <p className="text-base font-sora font-bold text-[#E6F1FF]">
                      {contract!.token_symbol}
                    </p>
                    <p className="text-xs text-[#93A4B8]">{contract!.token_name}</p>
                  </div>
                  <div className="ml-auto text-right">
                    <span
                      className="text-xs px-2 py-1 rounded-lg font-semibold font-mono"
                      style={{ background: `${chain!.chain_color}20`, color: chain!.chain_color }}
                    >
                      {chain!.network_badge}
                    </span>
                  </div>
                </div>

                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-[#93A4B8]">Blockchain</span>
                    <span className="text-[#E6F1FF] font-medium">{chain!.chain_name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#93A4B8]">Network</span>
                    <span className="font-semibold" style={{ color: chain!.chain_color }}>{chain!.network_badge} · {chain!.chain_symbol}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#93A4B8]">Token Decimals</span>
                    <span className="text-[#E6F1FF]">{contract!.token_decimals}</span>
                  </div>
                  {claimAmount > 0 && (
                    <div className="flex items-center justify-between pt-1 border-t border-[rgba(255,255,255,0.06)]">
                      <span className="text-[#93A4B8]">Your Claim Amount</span>
                      <span className="text-[#00E5A8] font-bold">{claimAmount.toLocaleString()} {contract!.token_symbol}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Contract address */}
              <div className="bg-[rgba(255,255,255,0.02)] border border-[rgba(22,194,255,0.08)] rounded-lg p-3">
                <p className="text-xs text-[#93A4B8] mb-1">Smart Contract Address</p>
                <p className="text-xs font-mono text-[#16C2FF] break-all">{contract!.contract_address}</p>
              </div>

              {/* TX Status feedback */}
              {txStatus !== 'idle' && (
                <div className={`rounded-xl p-4 border ${
                  txStatus === 'confirmed' ? 'bg-[rgba(0,229,168,0.06)] border-[rgba(0,229,168,0.2)]' :
                  txStatus === 'failed' ? 'bg-[rgba(239,68,68,0.06)] border-[rgba(239,68,68,0.2)]' :
                  'bg-[rgba(22,194,255,0.06)] border-[rgba(22,194,255,0.15)]'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    {txStatus === 'preparing' && <Loader2 size={14} className="text-[#16C2FF] animate-spin" />}
                    {txStatus === 'waiting_wallet' && <Loader2 size={14} className="text-[#F59E0B] animate-spin" />}
                    {txStatus === 'submitted' && <Loader2 size={14} className="text-[#16C2FF] animate-spin" />}
                    {txStatus === 'confirmed' && <CheckCircle size={14} className="text-[#00E5A8]" />}
                    {txStatus === 'failed' && <AlertCircle size={14} className="text-red-400" />}
                    <span className={`text-xs font-semibold ${
                      txStatus === 'confirmed' ? 'text-[#00E5A8]' :
                      txStatus === 'failed' ? 'text-red-400' :
                      txStatus === 'waiting_wallet' ? 'text-[#F59E0B]' :
                      'text-[#16C2FF]'
                    }`}>
                      {txStatus === 'preparing' && 'Preparing transaction…'}
                      {txStatus === 'waiting_wallet' && 'Waiting for wallet approval…'}
                      {txStatus === 'submitted' && 'Transaction submitted, confirming…'}
                      {txStatus === 'confirmed' && 'Transaction confirmed! 🎉'}
                      {txStatus === 'failed' && 'Transaction failed'}
                    </span>
                  </div>
                  {txHash && (
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-mono text-[#93A4B8] truncate flex-1">{txHash.slice(0, 20)}…{txHash.slice(-8)}</p>
                      <button onClick={copyTxHash} className="p-1 text-[#93A4B8] hover:text-[#16C2FF] transition-colors">
                        {copied ? <Check size={12} className="text-[#00E5A8]" /> : <Copy size={12} />}
                      </button>
                      {explorerUrl && (
                        <a href={explorerUrl} target="_blank" rel="noopener noreferrer" className="p-1 text-[#93A4B8] hover:text-[#16C2FF] transition-colors">
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                  )}
                  {txError && <p className="text-xs text-red-400 mt-1 leading-relaxed">{txError}</p>}
                </div>
              )}

              {/* Info notice */}
              {txStatus === 'idle' && (
                <div className="flex gap-2 bg-[rgba(22,194,255,0.05)] border border-[rgba(22,194,255,0.1)] rounded-lg p-3">
                  <Info size={14} className="text-[#16C2FF] flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-[#93A4B8]">
                    Your tokens will be distributed on the <strong className="text-[#E6F1FF]">{chain!.chain_name}</strong> blockchain.
                    Make sure your wallet supports <strong className="text-[#E6F1FF]">{chain!.network_badge}</strong> tokens before proceeding.
                  </p>
                </div>
              )}

              {/* Wallet required notice */}
              {!walletAddress && txStatus === 'idle' && (
                <div className="flex gap-2 bg-[rgba(245,158,11,0.05)] border border-[rgba(245,158,11,0.2)] rounded-lg p-3">
                  <AlertCircle size={14} className="text-[#F59E0B] flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-[#93A4B8]">Connect your wallet to claim tokens on-chain.</p>
                </div>
              )}

              {/* Actions */}
              {(txStatus === 'idle' || txStatus === 'failed') && (
                <div className="flex gap-3 pt-1">
                  {walletAddress ? (
                    <button
                      onClick={handleClaim}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-[#16C2FF] to-[#0EA5E9] text-[#07111F] font-semibold rounded-xl text-sm hover:opacity-90 transition-opacity disabled:opacity-50"
                    >
                      <Zap size={15} />
                      {txStatus === 'failed' ? 'Retry Claim' : 'Claim DARAM Tokens'}
                    </button>
                  ) : (
                    <button
                      onClick={onProceed}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-[#16C2FF] to-[#0EA5E9] text-[#07111F] font-semibold rounded-xl text-sm hover:opacity-90 transition-opacity"
                    >
                      <CheckCircle size={15} />
                      Proceed to Claim
                    </button>
                  )}
                  <button
                    onClick={onClose}
                    className="px-4 py-2.5 text-sm text-[#93A4B8] hover:text-[#E6F1FF] border border-[rgba(255,255,255,0.06)] rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              )}

              {txStatus === 'confirmed' && (
                <button
                  onClick={onClose}
                  className="w-full py-2.5 bg-[rgba(0,229,168,0.1)] border border-[rgba(0,229,168,0.2)] text-[#00E5A8] font-semibold rounded-xl text-sm hover:opacity-90 transition-opacity"
                >
                  Close
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
